import { uniqBy } from 'lodash';
import moment, { Moment } from 'moment';
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PencilService from 'services/PencilService';
import {
  CLIENT_TYPE,
  FinanceType,
  STICKER_FORMAT,
  WebStep,
  WebStepNames,
} from '@upstart/harmony/lib/legos/constants';
import { Pencil } from '@upstart/harmony/lib/legos/pencilTypes';
import {
  ShareDealDialogSelectedStickerOffers,
  ShareDealDialogStep,
  ShareDealDialogStickerConfig,
  ShareDealDialogStickerOffers,
} from '@upstart/desking-components';
import { Deal } from '@upstart/harmony/lib/legos/dealTypes';
import { Car } from '@upstart/harmony/lib/legos/carTypes';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes';
import { isInStoreDeal } from '@upstart/harmony/lib/pen-utils';
import { getAllFinanceTypesForCar } from '@upstart/harmony/lib/deal-utils';
import {
  FlowConfigSection,
  Website,
} from '@upstart/harmony/lib/legos/websiteTypes';
import { getDefaultWebFlow } from '@upstart/harmony/lib/flow-utils';
import { Feature, useGrowthBook } from 'hooks/GrowthBook';
import DeskingService from 'services/DeskingService';
import {
  getSendingSticker,
  getSendingStickerError,
} from '../../redux/deals/selectors';
import { getWebsite } from '../../redux/websites/selectors';
import { websiteRequest } from '../../redux/websites/actions';

export const ALL_SELECTED_VALUE = 'all';
export const getStickerOffersSource = (
  data: Pencil['data'],
  allowedFinanceTypes: FinanceType[],
): ShareDealDialogStickerOffers<string> => {
  const cash =
    data?.cash?.length && allowedFinanceTypes.includes(FinanceType.CASH)
      ? {
          label: 'Include cash',
          value: 'cash',
        }
      : null;
  const purchases = [];
  const leaseMileages = [];
  const leaseTerms = [];
  if (
    data?.purchase?.length &&
    allowedFinanceTypes.includes(FinanceType.PURCHASE)
  ) {
    purchases.push({
      label: 'All terms',
      value: ALL_SELECTED_VALUE,
    });

    data.purchase.forEach(offer => {
      purchases.push({
        label: `${offer.months} months`,
        value: `${offer.months}`,
      });
    });
  }

  if (data?.lease?.length && allowedFinanceTypes.includes(FinanceType.LEASE)) {
    leaseMileages.push({
      label: 'All mileages',
      value: ALL_SELECTED_VALUE,
    });
    leaseTerms.push({
      label: 'All terms',
      value: ALL_SELECTED_VALUE,
    });
    data.lease.forEach(offer => {
      leaseMileages.push({
        label: formatLeaseMileage(offer.mileage),
        value: `${offer.mileage}`,
      });
      leaseTerms.push({
        label: `${offer.months} months`,
        value: `${offer.months}`,
      });
    });
  }

  // Desking allows for multiple offers with the same term/mileage but we dont
  // want to show duplicate checkboxes
  return {
    cash,
    purchases: uniqBy(purchases, 'value'),
    leaseMileages: uniqBy(leaseMileages, 'value'),
    leaseTerms: uniqBy(leaseTerms, 'value'),
  };
};

export const formatLeaseMileage = (mileage: number) =>
  `${String(mileage).replace(/000$/, 'k')} miles`;

export const buildPencilForRequest = (
  startingPencil: Pencil,
  selectedOffers: ShareDealDialogSelectedStickerOffers<string>,
): Pencil => {
  const hasAllSelected = {
    leaseTerms: selectedOffers.leaseTerms.includes(ALL_SELECTED_VALUE),
    leaseMileages: selectedOffers.leaseMileages.includes(ALL_SELECTED_VALUE),
    purchases: selectedOffers.purchases.includes(ALL_SELECTED_VALUE),
  };

  const purchase = startingPencil.data?.purchase?.filter(
    pencilOffer =>
      hasAllSelected.purchases ||
      selectedOffers.purchases.includes(String(pencilOffer.months)),
  );

  const lease = startingPencil.data?.lease?.filter(pencilOffer => {
    const isTermSelected =
      hasAllSelected.leaseTerms ||
      selectedOffers.leaseTerms.includes(String(pencilOffer.months));
    const isMileageSelected =
      hasAllSelected.leaseMileages ||
      selectedOffers.leaseMileages.includes(String(pencilOffer.mileage));
    return isTermSelected && isMileageSelected;
  });

  const cash = selectedOffers.cash ? startingPencil.data?.cash : [];

  return {
    ...startingPencil,
    data: {
      ...startingPencil.data,
      purchase,
      cash,
      lease,
    },
  };
};

export const getEmptySelectedOffers = (): ShareDealDialogSelectedStickerOffers<string> => ({
  purchases: [],
  leaseMileages: [],
  leaseTerms: [],
  cash: false,
});

export const useStickerUrl = (
  step: ShareDealDialogStep,
  pencilDataForRequest: Pencil,
  pencilStickerData: Pencil['stickerData'],
  deskingId: string,
): string => {
  const [stickerUrl, setStickerUrl] = useState('');
  const growthBook = useGrowthBook();

  const fetchStickerUrl = async () => {
    let url: string;
    if (
      deskingId &&
      growthBook.getFeatureValue(Feature.USE_DESKING_DATA_FROM_SERVER, false)
    ) {
      url = (
        await DeskingService.createSticker(deskingId, {
          disclaimer: pencilStickerData?.disclaimer,
          expirationDate: pencilStickerData?.expiryDate,
          stickerFormat: STICKER_FORMAT.JPG,
          selectedOffers: {
            cash: !!pencilDataForRequest.data?.cash?.length,
            purchases:
              pencilDataForRequest.data?.purchase?.map(({ months }) =>
                months.toString(),
              ) ?? [],
            leaseMileages:
              pencilDataForRequest.data?.lease?.map(({ mileage }) =>
                mileage.toString(),
              ) ?? [],
            leaseTerms:
              pencilDataForRequest.data?.lease?.map(({ months }) =>
                months.toString(),
              ) ?? [],
          },
        })
      ).data.stickerUrl;
    } else {
      const nextPencil = {
        ...pencilDataForRequest,
        stickerData: pencilStickerData,
      };
      url = (await PencilService.getSticker(nextPencil.dealId, nextPencil)).url;
    }
    if (step === 'preview') {
      setStickerUrl(url);
    }
  };

  // calling onPrint causes some references that fetchStickerUrl depends on to change,
  // making it difficult to compute stickerUrl declaratively without bad UX from it
  // changing while the preview is still being displayed.
  useEffect(() => {
    if (step === 'preview') {
      fetchStickerUrl();
    } else {
      setStickerUrl('');
    }
  }, [step]);

  return stickerUrl;
};

export function useCallbackAfterStickerSent(cb: () => void, isOpen: boolean) {
  const isSendingSticker: boolean = useSelector(getSendingSticker);
  const didStickerSendFail: boolean = !!useSelector(getSendingStickerError);
  const previousIsSendingValue = useRef<boolean>();

  useEffect(() => {
    if (!isOpen) {
      previousIsSendingValue.current = undefined;
    }
  }, [isOpen]);

  useEffect(() => {
    if (
      previousIsSendingValue.current &&
      !isSendingSticker &&
      !didStickerSendFail
    ) {
      cb();
    }
  }, [isSendingSticker, didStickerSendFail]);

  useEffect(() => {
    previousIsSendingValue.current = isSendingSticker;
  }, [isSendingSticker]);
}

export function useStickerConfig(
  deal: Deal,
  car: Car,
  pencil: Pencil,
  dealer: Dealer,
): [
  ShareDealDialogStickerConfig<string, Moment>,
  Dispatch<SetStateAction<ShareDealDialogStickerConfig<string, Moment>>>,
] {
  const offerCheckboxSource = useMemo(() => {
    const clientType = isInStoreDeal(deal)
      ? CLIENT_TYPE.IN_STORE
      : CLIENT_TYPE.ONLINE;
    const allowedFinanceTypes = getAllFinanceTypesForCar({
      car,
      deal,
      dealer,
      clientType,
    });

    return getStickerOffersSource(pencil.data, allowedFinanceTypes);
  }, [car, deal, pencil.data, dealer]);

  const [stickerConfigWithoutSource, setStickerConfig] = useState<
    ShareDealDialogStickerConfig<string, Moment>
  >({
    includeWhenSending: true,
    disclaimer: '',
    offersSource: offerCheckboxSource,
    selectedOffers: getEmptySelectedOffers(),
    expirationDate: moment(),
    minExpirationDate: moment(),
  });

  const stickerConfig = useMemo(
    () => ({
      ...stickerConfigWithoutSource,
      offersSource: offerCheckboxSource,
    }),
    [stickerConfigWithoutSource, offerCheckboxSource],
  );

  return [stickerConfig, setStickerConfig];
}

export interface StepSource {
  value: WebStep;
  label: string;
}

const NO_WEB_STEPS: Array<StepSource> = [];
const WEB_STEP_VALUES = Object.values(WebStep);
const isWebStep = (value: string): value is WebStep =>
  WEB_STEP_VALUES.includes(value as WebStep);

export function useWebSteps(dealer: Dealer): StepSource[] {
  const websiteId = dealer.wizard?.enableProdigyWeb?.newWebsiteId;
  const website: Website | undefined | { id: null } = useSelector(getWebsite);
  const dispatch = useDispatch();
  useEffect(() => {
    if (websiteId && website?.id !== websiteId) {
      dispatch(websiteRequest({ websiteId }));
    }
  }, [websiteId]);

  return useMemo(() => {
    if (!website?.id) {
      return NO_WEB_STEPS;
    }

    const websiteFlow: FlowConfigSection[] =
      website.flow || getDefaultWebFlow();
    // filter out the special non-displayable piiInFlow key
    const enabledWebSteps = websiteFlow
      .filter(({ id }) => isWebStep(id))
      .map(({ id }) => {
        const webStep = id as WebStep;
        return {
          value: webStep,
          label: WebStepNames[webStep],
        };
      });

    return enabledWebSteps || NO_WEB_STEPS;
  }, [website]);
}
